// Shared shape + fetch for GET /pricing.json (this app's own backend, which
// caches the control plane's public GET /api/v1/pricing — see
// server/src/cloudish.js#getPricing). Used by both the marketing calculator
// and the /claim purchase flow, so it lives here rather than in either.

import { useEffect, useState } from 'react';

export interface Pricing {
  credit: { usd: number };
  compute: {
    rateCpuCorePerSecond: number;
    rateMemGbPerSecond: number;
    rateFlatPerSecond: number;
    profitMargin: number;
  };
  defaults: {
    docker: { cpuCores: number; memoryGb: number };
    build: { cpuCores: number; memoryGb: number };
  };
  freeGrant: number;
  freeGrantDailyCap: number | null;
}

export interface InstanceSize {
  name: string;
  cpuCores: number;
  memoryGb: number;
  /** What the size can use when the machine has it spare, on top of what
   *  it's billed for (shared only). */
  burst?: { cpuCores: number; memoryGb: number };
}

/** Mirrors the control plane's INSTANCE_SIZES and BURST_LIMITS
 *  (server/src/models/docker.js, the source of /instances.md). pricing.json only has rates, not sizes, so a
 *  new size there needs a line here too. Smallest first: INSTANCE_SIZES[0]
 *  is the one the marketing page quotes (PricingCalculator, InstancePricing). */
export const INSTANCE_SIZES: InstanceSize[] = [
  { name: 'shared', cpuCores: 0.1, memoryGb: 0.2, burst: { cpuCores: 1, memoryGb: 1 } },
  { name: 'small', cpuCores: 0.5, memoryGb: 1 },
  { name: 'medium', cpuCores: 1, memoryGb: 2 },
  { name: 'large', cpuCores: 2, memoryGb: 4 },
];

/** Credits per second for a container of this size, margin included — the
 *  same formula as the control plane's credits/pricing.js#creditsForCompute. */
export function costPerSecond(p: Pricing, cpuCores: number, memoryGb: number): number {
  const raw = cpuCores * p.compute.rateCpuCorePerSecond + memoryGb * p.compute.rateMemGbPerSecond + p.compute.rateFlatPerSecond;
  return raw * (1 + p.compute.profitMargin);
}

declare global {
  // eslint-disable-next-line no-var
  var __INITIAL_PRICING__: Pricing | null | undefined;
}

/** Seeded by entry-server.tsx's render() before it renders to a string, and
 *  serialized into the prerendered HTML as an inline script (see
 *  scripts/prerender.mjs) so the client's first render — before hydration's
 *  own effect refetches live — starts from the exact same value the server
 *  rendered, avoiding a hydration mismatch and a blank pricing slider in
 *  the no-JS/prerendered HTML. */
function initialPricing(): Pricing | null {
  return globalThis.__INITIAL_PRICING__ ?? null;
}

export function usePricing() {
  const [pricing, setPricing] = useState<Pricing | null>(initialPricing);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/pricing.json')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('unavailable'))))
      .then((data) => {
        if (!cancelled) setPricing(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { pricing, error };
}
